import { RefObject, useEffect, useRef } from "react";
import { useScroll, useMotionValueEvent } from "motion/react";

export const useMousePositionRef = (
  containerRef?: RefObject<HTMLElement | SVGElement>
) => {
  const positionRef = useRef({ x: 0, y: 0 });
  const lastMousePos = useRef({ x: 0, y: 0 });
  const lastUpdateTimeRef = useRef(0);
  const lastScrollUpdateRef = useRef(0);
  // const debounceMs = 16; // ~60fps
  const debounceMs = 50; // ~60fps

  const { scrollY } = useScroll();

  useEffect(() => {
    const updatePosition = (x: number, y: number) => {
      const now = performance.now();

      // Only update if enough time has passed
      if (now - lastUpdateTimeRef.current < debounceMs) {
        return;
      }

      lastUpdateTimeRef.current = now;
      lastMousePos.current = { x, y };

      if (containerRef?.current) {
        const rect = containerRef.current.getBoundingClientRect();
        positionRef.current = {
          x: x - rect.left,
          y: y - rect.top,
        };
      } else {
        positionRef.current = { x, y };
      }
    };

    const handleMouseMove = (ev: MouseEvent) => {
      updatePosition(ev.clientX, ev.clientY);
    };

    const handleTouchMove = (ev: TouchEvent) => {
      const touch = ev.touches[0];
      updatePosition(touch.clientX, touch.clientY);
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: true });

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("touchmove", handleTouchMove);
    };
  }, [containerRef]);

  // Update position on scroll using last known mouse position
  useMotionValueEvent(scrollY, "change", () => {
    const now = performance.now();

    // Debounce scroll updates
    if (now - lastScrollUpdateRef.current < debounceMs) {
      return;
    }

    lastScrollUpdateRef.current = now;

    if (containerRef?.current) {
      const rect = containerRef.current.getBoundingClientRect();
      positionRef.current = {
        x: lastMousePos.current.x - rect.left,
        y: lastMousePos.current.y - rect.top,
      };
    }
  });

  return positionRef;
};
